import { CountryCode } from '@constants'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiCampaignCheckoutDisplayFields } from '@hooks-api/fortune-fastpass/useApiCampaignCheckoutDisplayFields'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { HStack } from '@pets-ui/HStack/HStack'
import { Input } from '@pets-ui/Input/Input'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { VStack } from '@pets-ui/VStack/VStack'
import { Controller, useFormContext } from 'react-hook-form'
import { Asterisk } from '../../../../../components/Asterisk/Asterisk'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { partnerDisable } from '../../../../fp-b2b-shared/utils/partner-disable'

export function AddressTwReceiver() {
  const { campaignCode, language } = useCheckout()
  const { destructuredData: checkoutFields } = useApiCampaignCheckoutDisplayFields(
    { code: campaignCode, country_code: CountryCode.TW, language },
  )
  const { watch, control, setValue, trigger } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isPartner = partnerDisable({ roles: userInfo?.roles || [] })

  const addressState = Object.keys(checkoutFields?.available_states ?? {})
  const addressCity = formValues.billing_address_state && checkoutFields
    ? checkoutFields.available_states[formValues.billing_address_state]?.map((city: string) => city.split(' ')[1])
    : []

  const handleChangeState = (state: string) => {
    setValue('billing_address_state', state)
    setValue('billing_address_city', '')
    setValue('billing_address_postcode', '')
    trigger('billing_address_state')
  }

  const handleChangeCity = (city: string) => {
    const postcode = checkoutFields?.available_states[formValues.billing_address_state]?.find((cityAndPostcode: string) => cityAndPostcode.includes(city))?.split(' ')[0] || ''
    setValue('billing_address_city', city)
    setValue('billing_address_postcode', postcode)
    trigger('billing_address_city')
  }

  return (
    <VStack gap={4}>
      <HStack gap={2} flexWrap="nowrap">
        <VStack gap={1} flex="1">
          <Text variant="body">
            縣市
            <Asterisk />
          </Text>
          <Controller
            control={control}
            name="billing_address_state"
            rules={{ required: '此為必填欄位' }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Select
                {...field}
                fullWidth
                value={formValues.billing_address_state}
                rootRef={ref}
                error={!!error}
                helpText={error?.message}
                onChange={state => handleChangeState(state)}
                disablePortal={false}
                disabled={isPartner}
              >
                {addressState?.map(state => (
                  <SelectOption key={state} value={state}>
                    {state}
                  </SelectOption>
                ))}
              </Select>
            )}
          />
        </VStack>
        <VStack gap={1} flex="1">
          <Text variant="body">
            鄉鎮市區
            <Asterisk />
          </Text>
          <Controller
            control={control}
            rules={{ required: '此為必填欄位' }}
            name="billing_address_city"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Select
                {...field}
                fullWidth
                value={formValues.billing_address_city}
                rootRef={ref}
                error={!!error}
                helpText={error?.message}
                onChange={city => handleChangeCity(city)}
                disablePortal={false}
                disabled={isPartner}
              >
                {/* @ts-expect-error - TODO: fix this */}
                {addressCity?.map(city => (
                  // @ts-expect-error - TODO: fix this
                  <SelectOption key={city} value={city}>
                    {city}
                  </SelectOption>
                ))}
              </Select>
            )}
          />
        </VStack>
      </HStack>
      <VStack gap={1} flex="1">
        <Text variant="body">
          街道地址
          <Asterisk />
        </Text>
        <Controller
          control={control}
          name="billing_address_address1"
          rules={{ required: '此為必填欄位' }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Input
              {...field}
              value={formValues.billing_address_address1 || ''}
              rootRef={ref}
              fullWidth
              error={!!error}
              helpText={error?.message}
              placeholder="請輸入街道地址"
              disabled={isPartner}
            />
          )}
        />
      </VStack>
    </VStack>
  )
}
